// Deterministic PRNG (mulberry32) + zipfian sampler so workloads are
// reproducible from a seed — the same seed replays the same op stream.

export class Rng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  next(): number {
    let t = (this.s += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  int(maxExclusive: number): number {
    return Math.floor(this.next() * maxExclusive);
  }
}

/**
 * Zipfian sampler over ranks [0, n) using the rejection-inversion method
 * (Gray et al. approximation): rank 0 is hottest.
 */
export class Zipf {
  private readonly zetan: number;
  private readonly theta: number;
  private readonly alpha: number;
  private readonly eta: number;

  constructor(
    private readonly n: number,
    private readonly rng: Rng,
    theta = 0.99,
  ) {
    this.theta = theta;
    let z = 0;
    for (let i = 1; i <= n; i++) z += 1 / Math.pow(i, theta);
    this.zetan = z;
    this.alpha = 1 / (1 - theta);
    let zeta2 = 0;
    for (let i = 1; i <= 2; i++) zeta2 += 1 / Math.pow(i, theta);
    this.eta = (1 - Math.pow(2 / n, 1 - theta)) / (1 - zeta2 / this.zetan);
  }

  next(): number {
    const u = this.rng.next();
    const uz = u * this.zetan;
    if (uz < 1) return 0;
    if (uz < 1 + Math.pow(0.5, this.theta)) return 1;
    return Math.floor(
      this.n * Math.pow(this.eta * u - this.eta + 1, this.alpha),
    );
  }
}
